// Models
import { FormFieldCustomSchema, FormFieldSchema } from 'app/shared/models/form';
import { FullCaseResponse } from 'app/modules/cases/responses';
import { ValueField } from 'app/modules/search/helpers';
import {
  ActionEventExternalId,
  TxnEventExternalId,
} from 'app/shared/types/eventExternalIds';


// Components
import {
  U21EntitySearch,
  U21TransactionEventSearch,
  U21TagSelect,
  U21ActionEventSearch,
} from 'app/shared/u21-ui/components/dashboard';

// Utils
import emptyFn from 'app/shared/utils/empty-fn';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';
import { U21AlertSearch } from 'app/shared/u21-ui/components/dashboard/U21AlertSearch';
import { U21InstrumentSearch } from 'app/shared/u21-ui/components/dashboard/U21InstrumentSearch';
import { U21AgentSearch } from 'app/shared/u21-ui/components/dashboard/U21AgentSearch';
import { AgentStatus } from 'app/modules/agents/models';

export interface CaseCreateDetailsFormValue {
  alerts: number[];
  entities: (number | string)[];
  events: TxnEventExternalId[];
  action_events: ActionEventExternalId[];
  instruments: (number | string)[];
}

export const CASE_TAGS: FormFieldCustomSchema<number[]> = {
  children: <U21TagSelect label={undefined} onChange={() => {}} creatable />,
  label: 'Tags',
  name: 'tags',
  type: 'CUSTOM',
  fieldProps: {
    placeholder: 'Add tags',
  },
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.tags?.map((tag) => tag.id) ?? [],
};

export const CASE_WATCHERS: FormFieldCustomSchema<number[]> = {
  children: (
    <U21AgentSearch
      allowedStatuses={[AgentStatus.ACTIVE]}
      label={undefined}
      multi
      onChange={() => {}}
    />
  ),
  fieldProps: {
    placeholder: 'Add agents',
  },
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.watchers?.map((watcher) => watcher.id) ?? [],
  label: 'Watchers',
  name: 'watchers',
  type: 'CUSTOM',
};

export const CASE_ALERTS: FormFieldCustomSchema<number[]> = {
  children: <U21AlertSearch onChange={emptyFn} clearable={false} />,
  label: 'Alerts',
  name: 'alerts' satisfies keyof CaseCreateDetailsFormValue,
  fieldProps: {
    placeholder: 'Add alerts',
    multi: true,
  },
  type: 'CUSTOM',
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.alerts?.map((alert) => alert.id) ?? [],
};

export const CASE_ENTITIES: FormFieldCustomSchema<(number | string)[]> = {
  children: (
    <U21EntitySearch
      onChange={() => {}}
      selectProps={{ label: undefined }}
      searchProps={{
        placeholder: 'Search entities by entity ID',
      }}
      valueField={ValueField.EXTERNAL_ID}
      clearable={false}
    />
  ),
  label: 'Entities',
  name: 'entities' satisfies keyof CaseCreateDetailsFormValue,
  type: 'CUSTOM',
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.entities?.map((entity) => entity.external_id) ?? [],
};

export const CASE_LINK_ALL_RELATED_ENTITIES: FormFieldSchema<boolean> = {
  name: 'add_related_entities',
  fieldProps: {
    label: `Link all entities from associated alerts to this case`,
  },
  type: U21FormFieldTypes.CHECKBOX,
};

export const CASE_TRANSACTIONS: FormFieldCustomSchema<TxnEventExternalId[]> = {
  children: (
    <U21TransactionEventSearch
      onChange={() => {}}
      label={undefined}
      placeholder="Search transactions by unit21 ID (Requires exact match)"
      clearable={false}
      multi
    />
  ),
  label: 'Transaction events',
  name: 'events' satisfies keyof CaseCreateDetailsFormValue,
  type: 'CUSTOM',
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.events?.map((event) => event.external_id) ?? [],
};

export const CASE_LINK_ALL_RELATED_TRANSACTIONS: FormFieldSchema<boolean> = {
  name: 'add_related_txn_events',
  fieldProps: {
    label: `Link all transaction events from associated alerts to this case`,
  },
  type: U21FormFieldTypes.CHECKBOX,
};

export const CASE_ACTION_EVENTS: FormFieldCustomSchema<
  ActionEventExternalId[]
> = {
  children: (
    <U21ActionEventSearch
      onChange={() => {}}
      label={undefined}
      placeholder="Search action events by unit21 ID (Requires exact match)"
      clearable={false}
      multi
    />
  ),
  label: 'Action events',
  name: 'action_events' satisfies keyof CaseCreateDetailsFormValue,
  type: 'CUSTOM',
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.action_events?.map((event) => event.external_id) ?? [],
};

export const CASE_INSTRUMENTS: FormFieldCustomSchema<(number | string)[]> = {
  children: (
    <U21InstrumentSearch
      onChange={() => {}}
      placeholder="Search instruments by instrument ID"
      clearable={false}
      multi
    />
  ),
  label: 'Instruments',
  name: 'instruments',
  type: 'CUSTOM',
  default: (caseDetail: FullCaseResponse | Record<string, never>) =>
    caseDetail?.instruments?.map((instrument) => instrument.external_id) ?? [],
};

export const CASE_LINK_ALL_RELATED_INSTRUMENTS: FormFieldSchema<boolean> = {
  name: 'add_related_instruments',
  fieldProps: {
    label: `Link all instruments from associated alerts to this case`,
  },
  type: U21FormFieldTypes.CHECKBOX,
};

export const CASE_DETAILS_SCHEMA = [
  CASE_TAGS,
  CASE_WATCHERS,
  CASE_ALERTS,
  CASE_ENTITIES,
  CASE_TRANSACTIONS,
  CASE_ACTION_EVENTS,
  CASE_INSTRUMENTS,
];
